import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faTasks,
  faTachometer,
  faPlus,
  faArrowLeft,
  faCheckCircle,
  faUsers,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";

import {
  EQUIPMENT_OPTIONS,
  USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS,
  BREATHWORK_TYPE_WITH_EMPTY_OPTIONS,
} from "../../../Constants/FieldOptions";
import { postSessionCreateAPI } from "../../../API/Session";

import {
  topAlertMessageState,
  topAlertStatusState,
  userDetailState,
} from "../../../AppState";
import FormErrorBox from "../../../Components/Reusable/FormErrorBox";
import FormInputField from "../../../Components/Reusable/FormInputField";
import FormDateTimeField from "../../../Components/Reusable/FormDateTimeField";
import FormTextareaField from "../../../Components/Reusable/FormDateTimeField";
import FormMultiSelectField from "../../../Components/Reusable/FormMultiSelectField";
import FormRadioField from "../../../Components/Reusable/FormRadioField";
import FormSelectField from "../../../Components/Reusable/FormSelectField";

function OrgUserSessionAdd() {
  ////
  //// URL Parameters.
  ////

  const { uid } = useParams();

  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);
  const [user, setUser] = useRecoilState(userDetailState);

  ////
  //// Component states.
  ////

  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [forceURL, setForceURL] = useState("");
  const [no, setNo] = useState();
  const [completedTime, setCompletedTime] = useState();
  const [duration, setDuration] = useState();
  const [totalDuration, setTotalDuration] = useState();
  const [name, setName] = useState();
  const [description, setDescription] = useState();
  const [brief, setBrief] = useState();
  const [equipment, setEquipment] = useState([]);
  const [isPrimarySession, setIsPrimarySession] = useState(false);
  const [targetHeartRate, setTargetHeartRate] = useState();
  const [targetHeartRateRatio, setTargetHeartRateRatio] = useState();
  const [sessionType, setSessionType] = useState();
  const [motivationalText, setMotivationalText] = useState();
  const [feeling, setFeeling] = useState();
  const [breathworkType, setBreathworkType] = useState("");

  ////
  //// Event handling.
  ////

  const onEquipmentChange = (e) => {
    console.log("onEquipmentChange, e:", e);
    let values = [];
    for (let option of e) {
      console.log("option:", option);
      values.push(option.value);
    }
    console.log("onEquipmentChange, values:", values);
    setEquipment(values);
  };

  const onSubmitClick = (e) => {
    console.log("onSubmitClick: Beginning...");
    const session = {
      userID: uid,
      no: parseInt(no),
      completedTime: completedTime,
      duration: duration,
      totalDuration: totalDuration,
      name: name,
      description: description,
      brief: brief,
      equipment: equipment,
      targetHeartRate: targetHeartRate,
      targetHeartRateRatio: parseFloat(targetHeartRateRatio),
      sessionType: sessionType,
      motivationalText: motivationalText,
      feeling: feeling,
      breathworkType: breathworkType,
    };
    console.log("onSubmitClick, session:", session);
    postSessionCreateAPI(
      session,
      onSessionCreateSuccess,
      onSessionCreateError,
      onSessionCreateDone
    );
  };

  ////
  //// API.
  ////

  function onSessionCreateSuccess(response) {
    // For debugging purposes only.
    console.log("onSessionCreateSuccess: Starting...");
    console.log(response);

    // Add a temporary banner message in the app and then clear itself after 2 seconds.
    setTopAlertMessage("Session created");
    setTopAlertStatus("success");
    setTimeout(() => {
      console.log("onSessionCreateSuccess: Delayed for 2 seconds.");
      console.log(
        "onSessionCreateSuccess: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // Redirect the user to a new page.
    setForceURL("/user/" + response.userId + "/session/" + response.id);
  }

  function onSessionCreateError(apiErr) {
    console.log("onSessionCreateError: Starting...");
    setErrors(apiErr);

    // Add a temporary banner message in the app and then clear itself after 2 seconds.
    setTopAlertMessage("Failed submitting");
    setTopAlertStatus("danger");
    setTimeout(() => {
      console.log("onSessionCreateError: Delayed for 2 seconds.");
      console.log(
        "onSessionCreateError: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onSessionCreateDone() {
    console.log("onSessionCreateDone: Starting...");
    setFetching(false);
  }

  ////
  //// Misc.
  ////

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0); // Start the page at the top of the page.
    }

    return () => {
      mounted = false;
    };
  }, []);

  ////
  //// Component rendering.
  ////

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="">
                <Link to="/users" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faUsers} />
                  &nbsp;Users
                </Link>
              </li>
              <li class="">
                <Link to={`/user/${uid}/sessions`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faEye} />
                  &nbsp;Detail (Sessions)
                </Link>
              </li>
              <li class="is-active">
                <Link aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faPlus} />
                  &nbsp;New
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/user/${uid}/sessions`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Detail (Sessions)
                </Link>
              </li>
            </ul>
          </nav>

          {/* Page content */}
          <nav class="box">
            <article class="message is-danger">
              <div class="message-body">
                <FontAwesomeIcon className="mdi" icon={faExclamationTriangle} />
                &nbsp;This functionality is only for testing purposes.
              </div>
            </article>

            <p class="title is-4">
              <FontAwesomeIcon className="fas" icon={faTasks} />
              &nbsp;Session - {user.firstName} {user.lastName}
            </p>
            <FormErrorBox errors={errors} />

            <p class="subtitle is-6">Detail</p>

            {isFetching && (
              <div class="columns is-centered" style={{ paddingTop: "20px" }}>
                <div class="column has-text-centered is-2">
                  <div class="loader-wrapper is-centered">
                    <div
                      class="loader is-loading is-centered"
                      style={{ height: "80px", width: "80px" }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {!isFetching && (
              <div class="container">
                <FormInputField
                  label="No"
                  name="no"
                  type="number"
                  placeholder="#"
                  value={no}
                  errorText={errors && errors.no}
                  helpText=""
                  onChange={(e) => setNo(parseInt(e.target.value))}
                  isRequired={true}
                  maxWidth="75px"
                />

                <FormDateTimeField
                  label="Completed Time (Optional)"
                  name="completedTime"
                  placeholder="Date input"
                  value={completedTime}
                  errorText={errors && errors.completedTime}
                  helpText="The date/time this session was completed on"
                  onChange={(date) => setCompletedTime(date)}
                  isRequired={true}
                  maxWidth="350px"
                />

                <FormInputField
                  label="Duration"
                  name="duration"
                  type="number"
                  placeholder="#"
                  value={duration}
                  errorText={errors && errors.duration}
                  helpText="MEASURED IN MINUTES"
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  isRequired={true}
                  maxWidth="100px"
                />

                <FormInputField
                  label="Total Duration (Optional)"
                  name="totalDuration"
                  type="number"
                  placeholder="#"
                  value={totalDuration}
                  errorText={errors && errors.totalDuration}
                  helpText="MEASURED IN MINUTES"
                  onChange={(e) => setTotalDuration(parseInt(e.target.value))}
                  isRequired={true}
                  maxWidth="100px"
                />

                <FormInputField
                  label="Name"
                  name="name"
                  placeholder="Text input"
                  value={name}
                  errorText={errors && errors.name}
                  helpText=""
                  onChange={(e) => setName(e.target.value)}
                  isRequired={true}
                  maxWidth="350px"
                />

                <FormTextareaField
                  label="Description"
                  name="description"
                  placeholder="Text input"
                  value={description}
                  errorText={errors && errors.description}
                  helpText=""
                  onChange={(e) => setDescription(e.target.value)}
                  isRequired={true}
                  maxWidth="100%"
                  rows={4}
                />

                <FormTextareaField
                  label="Brief"
                  name="brief"
                  placeholder="Text input"
                  value={brief}
                  errorText={errors && errors.brief}
                  helpText=""
                  onChange={(e) => setBrief(e.target.value)}
                  isRequired={true}
                  maxWidth="100%"
                  rows={2}
                />

                <FormTextareaField
                  label="Motivational Text (Optional)"
                  name="motivationalText"
                  placeholder="Text input"
                  value={motivationalText}
                  errorText={errors && errors.motivationalText}
                  helpText=""
                  onChange={(e) => setMotivationalText(e.target.value)}
                  isRequired={true}
                  maxWidth="100%"
                  rows={2}
                />

                <FormMultiSelectField
                  label="Equipment (Optional)"
                  name="equipment"
                  placeholder="Text input"
                  options={EQUIPMENT_OPTIONS}
                  selectedValues={equipment}
                  onChange={onEquipmentChange}
                  errorText={errors && errors.equipment}
                  helpText="Pick the equipment associated with this session template."
                  isRequired={true}
                  maxWidth="320px"
                />

                <FormInputField
                  label="Target Heart Rate (Optional)"
                  name="targetHeartRate"
                  type="number"
                  placeholder="Number input"
                  value={targetHeartRate}
                  errorText={errors && errors.targetHeartRate}
                  helpText=""
                  onChange={(e) => setTargetHeartRate(parseInt(e.target.value))}
                  isRequired={true}
                  maxWidth="95px"
                />

                <FormInputField
                  label="Target Heart Rate Ratio"
                  name="targetHeartRateRatio"
                  type="number"
                  placeholder="Number input"
                  value={targetHeartRateRatio}
                  errorText={errors && errors.targetHeartRateRatio}
                  helpText=""
                  onChange={(e) => setTargetHeartRateRatio(e.target.value)}
                  isRequired={true}
                  maxWidth="175px"
                />

                <FormRadioField
                  label="Session Type"
                  name="sessionType"
                  value={sessionType}
                  opt1Value="base"
                  opt1Label="Base"
                  opt2Value="breath"
                  opt2Label="Breath"
                  opt3Value="sleep"
                  opt3Label="Sleep"
                  errorText={errors && errors.sessionType}
                  onChange={(e) => setSessionType(e.target.value)}
                  maxWidth="180px"
                />

                <FormSelectField
                  label="Feeling"
                  name="feeling"
                  placeholder="Pick"
                  selectedValue={feeling}
                  errorText={errors && errors.feeling}
                  helpText=""
                  onChange={(e) => setFeeling(e.target.value)}
                  options={USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS}
                />

                <FormSelectField
                  label="Breathwork Type (Optional)"
                  name="breathworkType"
                  placeholder="Pick"
                  selectedValue={breathworkType}
                  errorText={errors && errors.breathworkType}
                  helpText=""
                  onChange={(e) => setBreathworkType(e.target.value)}
                  options={BREATHWORK_TYPE_WITH_EMPTY_OPTIONS}
                />

                <div class="columns">
                  <div class="column is-half">
                    <Link
                      to={`/user/${uid}/sessions`}
                      class="button is-hidden-touch"
                    >
                      <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                      &nbsp;Back
                    </Link>
                    <Link
                      to={`/user/${uid}/sessions`}
                      class="button is-fullwidth is-hidden-desktop"
                    >
                      <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                      &nbsp;Back
                    </Link>
                  </div>
                  <div class="column is-half has-text-right">
                    <button
                      class="button is-primary is-hidden-touch"
                      onClick={onSubmitClick}
                    >
                      <FontAwesomeIcon className="fas" icon={faCheckCircle} />
                      &nbsp;Save
                    </button>
                    <button
                      class="button is-primary is-fullwidth is-hidden-desktop"
                      onClick={onSubmitClick}
                    >
                      <FontAwesomeIcon className="fas" icon={faCheckCircle} />
                      &nbsp;Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default OrgUserSessionAdd;
